import {
  setAllAIRecomendation,
  setAllExactST,
  setAllSTLevels,
  setKewordDetails,
  setNegativeKeywordDetails,
  setNegetiveSearchTerm,
  setPositiveRecentSearchTerm,
  setPositiveSearchTerm,
} from "../../../features/aiRecomendationTabSlice";
import {
  setAccountsForSTOperation,
  setCampaignsWithoutScopeData,
  setNegativesInListAndInAdGroupData,
  setNegativesInTheCampaignData,
  setScopesOfSTOperation,
} from "../../../features/scopesTabSlice";
import {
  setCapturedByMultipleKeywords,
  setContradictingSTDefinition,
  setSTIdenticalToExactKeyword,
  setSTMissingCrossNegatives,
  setSTWithPerformanceUnClassified,
} from "../../../features/searchTermTabSlice";
import { setDisplayErrorSnackBar } from "../../../features/userSlice";
import { stOperationService } from "../../../services/api/search-term-operation.endpoints";
import store from "../../../store";

// get accounts
async function getAccounts(
  oauthTokenHeader: string,
  queryId: string,
  site_shared_uid: string,
  account_traffic_source_shared_uid: string | any,
  account_isactive: number | any,
) {
  try {
    const response = await stOperationService.getAccountsBasedOnSite(
      oauthTokenHeader,
      queryId,
      site_shared_uid,
      account_traffic_source_shared_uid,
      account_isactive,
    );
    if (!response || !response.status || response.status !== 200) {
      throw new Error("Failed to access accounts.");
    } else {
      const accounts = response?.data?.data?.query_output ?? [];
      // Allowed traffic sources
      const ALLOWED_SOURCES = new Set(["g_ads_search", "bing_ads_search"]);
      //filtered accounts from allowed traffic sources
      const filteredAccounts = accounts.filter((account: any) =>
        ALLOWED_SOURCES.has(account?.account_traffic_source),
      );
      store.dispatch(setAccountsForSTOperation(filteredAccounts)); // Set the fetched data
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  }
}

///------------------Scopes Tab Endpoints------------------///

// get scopes based on website and account
async function getScopes(
  oauthTokenHeader: string,
  website_shared_uid: string,
  account_shared_uid: string,
) {
  try {
    const response = await stOperationService.getAllScopes(
      oauthTokenHeader,
      website_shared_uid,
      account_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access scopes.");
    } else if (response.status === 200) {
      const scopesData = response?.data?.data ?? [];
      store.dispatch(setScopesOfSTOperation(scopesData)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

// get campaigns without scope
async function getCampaignsWithoutScope(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string,
) {
  try {
    const response = await stOperationService.getCampaignsWithoutScope(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      account_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access campaigns without scope.");
    } else if (response.status === 200) {
      const campaignsWithoutScope = response?.data?.data?.query_output ?? [];
      const filteredCampaigns = campaignsWithoutScope?.filter(
        (c: any) => c.cul_camp_status === "active",
      ); // only active unassigned campaigns
      store.dispatch(setCampaignsWithoutScopeData(filteredCampaigns)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

// get negative definition in both list and adgroup
async function getNegativeDefinitionBothListAndAdgroup(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string,
) {
  try {
    const response =
      await stOperationService.getNegativeDefinitionBothListAndAdgroup(
        oauthTokenHeader,
        queryId,
        website_shared_uid,
        account_shared_uid,
      );
    if (!response || !response.status) {
      throw new Error(
        "Failed to access negative definition in both list and adgroup.",
      );
    } else if (response.status === 200) {
      const negativesInListAndInAdGroup =
        response?.data?.data?.query_output ?? [];
      store.dispatch(
        setNegativesInListAndInAdGroupData(negativesInListAndInAdGroup),
      ); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

// get negative definition in the campaign
async function getNegativeDefinitionInCampaign(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string,
) {
  try {
    const response = await stOperationService.getNegativeDefinitionInCampaign(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      account_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access negative definition in the campaign.");
    } else if (response.status === 200) {
      const negativesInTheCampaign = response?.data?.data?.query_output ?? [];
      store.dispatch(setNegativesInTheCampaignData(negativesInTheCampaign)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

// create new scope
async function createNewScope(oauthTokenHeader: string, data: any) {
  try {
    const response = await stOperationService.createNewScope(
      oauthTokenHeader,
      data,
    );
    // Ensure response exists and check for both 200 and 201
    if (!response || !response.status) {
      return { reason: "Failed to create new scope." };
    }

    if (response.status === 200) {
      return response?.data;
    } else if (response.status === 201) {
      return response?.data;
    } else {
      return { reason: `Unexpected status code: ${response.status}` };
    }
  } catch (e: any) {
    return e?.parsedText || { reason: "Unknown error occurred." };
  }
}

// update existing scope name
async function updateExistingScope(
  oauthTokenHeader: string,
  data: any,
  scope_shared_uid: any,
) {
  try {
    const response = await stOperationService.editScope(
      oauthTokenHeader,
      data,
      scope_shared_uid,
    );
    // Ensure response exists and check for both 200 and 201
    if (!response || !response.status) {
      return { reason: "Failed to update scope." };
    }

    if (response.status === 200) {
      return response?.data;
    } else if (response.status === 201) {
      return response?.data;
    } else {
      return { reason: `Unexpected status code: ${response.status}` };
    }
  } catch (e: any) {
    return e?.parsedText || { reason: "Unknown error occurred." };
  }
}

// link between campaign to scope
async function linkBetweenCampaignToScope(oauthTokenHeader: string, data: any) {
  try {
    const response = await stOperationService.linkBetweenCampaignToScope(
      oauthTokenHeader,
      data,
    );
    // Ensure response exists and check for both 200 and 201
    if (!response || !response.status) {
      // return { reason: "Failed to link between campaign and scope." };
      return {
        success: false,
        status: null,
        reason: "Failed to link between campaign and scope.",
      };
    }

    if (response.status === 201) {
      //return response?.data;
      // All records created successfully
      return {
        success: true,
        status: 201,
        data: response.data,
      };
    } else if (response.status === 207) {
      //return response?.data;
      // Multi-status: some succeeded, some failed
      const responseData = response.data;
      const hasSuccesses = responseData?.data?.some(
        (item: any) => item.created === true || item.updated === true,
      );

      const hasFailures = responseData?.data?.some(
        (item: any) => item.created === false && item.updated === false,
      );

      return {
        success: hasSuccesses, // true if at least one succeeded
        status: 207,
        partialSuccess: hasSuccesses && hasFailures,
        allFailed: !hasSuccesses && hasFailures,
        data: responseData,
        message: responseData?.reason || "Multi-status response",
      };
    } else if (response.status === 400) {
      return {
        success: false,
        status: 400,
        reason: "Failed to link between campaign and scope.",
      };
    } else {
      return {
        success: false,
        status: response.status,
        reason: `Unexpected status code: ${response.status}`,
      };
    }
  } catch (e: any) {
    //return e?.parsedText || { reason: "Unknown error occurred." };
    return {
      success: false,
      status: e?.status || null,
      reason:
        e?.parsedText?.reason ||
        e?.message ||
        "Failed to link between campaign and scope.",
      error: e,
    };
  }
}

async function unlinkCampaigsFromCurrentScope(
  oauthTokenHeader: string,
  data: any,
) {
  try {
    const response = await stOperationService.unlinkCampaigsFromScope(
      oauthTokenHeader,
      data,
    );

    // Ensure response exists and has a status
    if (!response || !response.status) {
      return { reason: "Failed to unlink campaigns from scope." };
    }

    // 204: All campaigns unlinked successfully
    if (response.status === 204) {
      return { reason: "success" };
    }
    // 207: Partial success - some succeeded, some failed
    else if (response.status === 207) {
      const responseData = response.data;
      const dataArray = responseData?.data || [];

      // Separate successful and failed unlinking operations
      const successfulUnlinks = dataArray.filter(
        (item: any) => item.unlink === true,
      );
      const failedUnlinks = dataArray.filter(
        (item: any) => item.unlink === false,
      );

      return {
        reason: responseData?.reason || "Some records failed to unlink.",
        totalCount: dataArray.length,
        successCount: successfulUnlinks.length,
        failureCount: failedUnlinks.length,
        successfulUnlinks,
        failedUnlinks,
        partialSuccess: true,
      };
    }
    // Unexpected status code
    else {
      return { reason: `Unexpected status code: ${response.status}` };
    }
  } catch (e: any) {
    return e?.parsedText || { reason: "Unknown error occurred." };
  }
}

// remove negative terms
async function removeNegativeTerms(oauthTokenHeader: string, reqBody: any) {
  try {
    const response = await stOperationService.removeNegativeTerms(
      oauthTokenHeader,
      reqBody,
    );

    if (!response || !response.status) {
      throw new Error(
        "Failed to remove negative terms. Please try again later.",
      );
    } else if (response.status === 200) {
      return response.data;
    }
  } catch (e: any) {
    const message = { reason: "Failed to remove negative terms." }; // custom message for error handling
    return e ? e?.parsedText : message;
  }
}

//-------------------search term operation endpoint logic export-------------------//
async function getSTPerformanceUnclassified(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string,
) {
  try {
    const response = await stOperationService.getSTPerformanceUnclassified(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      account_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access unclassified search terms.");
    } else if (response.status === 200) {
      const unClassifiedSearchTerms = response?.data?.data?.query_output ?? [];
      store.dispatch(setSTWithPerformanceUnClassified(unClassifiedSearchTerms)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function createPositiveLinks(oauthTokenHeader: string, data: any) {
  try {
    const response = await stOperationService.createPositiveLinks(
      oauthTokenHeader,
      data,
    );
    if (!response || !response.status || response.status !== 201) {
      throw new Error("Failed to create positive links.");
    } else if (response.status === 201) {
      return response?.data;
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  }
}

async function createPositiveLinksMulti(oauthTokenHeader: string, data: any) {
  try {
    const response = await stOperationService.createPositiveLinks(
      oauthTokenHeader,
      data,
    );
    if (!response || !response.status) {
      throw new Error("Failed to create positive links.");
    } else if (response.status === 201) {
      return response;
    } else if (response.status === 207) {
      return response;
    }
  } catch (e: any) {
    throw e;
  }
}

async function addNegativeTermsToAdgroupUpdate(
  oauthTokenHeader: string,
  data: any,
) {
  try {
    const response = await stOperationService.addNegativeTermsToAdgroup(
      oauthTokenHeader,
      data,
    );

    // Ensure response exists and has a status
    if (!response || !response.status) {
      throw new Error("Failed to added as negative links.");
    }

    if (!response || !response.status) {
      throw new Error("Failed to added as negative links.");
    } else if (response.status === 200) {
      return response;
    } else if (response.status === 207) {
      return response;
    }
  } catch (e: any) {
    throw e;
  }
}
async function addNegativeTermsToAdgroup(oauthTokenHeader: string, data: any) {
  const totalTerms = data.terms?.length || 0;

  try {
    const response = await stOperationService.addNegativeTermsToAdgroup(
      oauthTokenHeader,
      data,
    );

    // Ensure response exists and has a status
    if (!response || !response.status) {
      return {
        reason: "Failed to add negative terms.",
        data: {
          summary: { total: totalTerms, successful: 0, failed: totalTerms },
          results: [],
        },
      };
    }

    const responseData = response.data;

    // Success responses (200, 201, 204)
    if ([200, 201, 204].includes(response.status)) {
      return {
        reason: responseData?.reason || "success",
        data: {
          summary: {
            total: responseData?.data?.summary?.total || totalTerms,
            successful: responseData?.data?.summary?.successful || totalTerms,
            failed: responseData?.data?.summary?.failed || 0,
          },
          results: responseData?.data?.results || [],
        },
      };
    }
    // 207: Partial success
    else if (response.status === 207) {
      return {
        reason: responseData?.reason || "Partial success",
        data: {
          summary: responseData?.data?.summary || {
            total: totalTerms,
            successful: 0,
            failed: totalTerms,
          },
          results: responseData?.data?.results || [],
        },
      };
    }
    // Unexpected status code
    else {
      return {
        reason: `Unexpected status code: ${response.status}`,
        data: {
          summary: { total: totalTerms, successful: 0, failed: totalTerms },
          results: [],
        },
      };
    }
  } catch (e: any) {
    let errorMessage = "Failed to add negative terms.";

    if (e?.status === 400 && e?.text) {
      try {
        const errorData = JSON.parse(e.text);
        errorMessage = errorData?.reason || "Bad Request";
      } catch (parseError) {
        errorMessage = e.text || "Bad Request";
      }
    } else if (e?.text) {
      try {
        const parsed = JSON.parse(e.text);
        errorMessage = parsed?.reason || errorMessage;
      } catch {}
    } else if (e?.message) {
      errorMessage = e.message;
    }

    // Re-throw the error so it can be caught by createNegativeSearchTermLinks
    throw new Error(errorMessage);
  }
}

async function getESTMissingCrossNegatives(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string,
) {
  try {
    const response = await stOperationService.getExclusiveMissingCrossNegatives(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      account_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access missing cross negatives.");
    } else if (response.status === 200) {
      const missingCrossNegatives = response?.data?.data?.query_output ?? [];
      store.dispatch(setSTMissingCrossNegatives(missingCrossNegatives)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function getContradictingSTDefinitions(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string,
) {
  try {
    const response = await stOperationService.getContradictingSTDefinitions(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      account_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access contradicting definitions.");
    } else if (response.status === 200) {
      const contradictingSTDefinition =
        response?.data?.data?.query_output ?? [];
      store.dispatch(setContradictingSTDefinition(contradictingSTDefinition)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function getSTOperationAIRecommendation(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string,
  scope_shared_uid: string,
) {
  try {
    const response = await stOperationService.getSTOperationAIRecommendation(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      account_shared_uid,
      scope_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access AI recommendation.");
    } else if (response.status === 200) {
      const responseData = response?.data?.data?.query_output ?? [];
      store.dispatch(setAllAIRecomendation(responseData));
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(setAllAIRecomendation([]));
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}
// get identical to exact keyword
async function getIdenticalToExactKeyword(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string,
) {
  try {
    const response = await stOperationService.getIdenticalToExactKeyword(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      account_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access exact keywords.");
    } else if (response.status === 200) {
      const identicalToExactKeyword = response?.data?.data?.query_output ?? [];
      store.dispatch(setSTIdenticalToExactKeyword(identicalToExactKeyword)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(setAllAIRecomendation([]));
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function getPositiveST(
  oauthTokenHeader: string,
  queryId: string,
  keyword_shared_uid: string,
  website_shared_uid: string,
) {
  try {
    const response = await stOperationService.getPositiveOrNegetiveST(
      oauthTokenHeader,
      queryId,
      keyword_shared_uid,
      website_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access Search Term");
    } else if (response.status === 200) {
      const responseData = response?.data?.data?.query_output ?? [];
      store.dispatch(setPositiveSearchTerm(responseData)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(setPositiveSearchTerm([]));
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function getNegetiveST(
  oauthTokenHeader: string,
  queryId: string,
  keyword_shared_uid: string,
  website_shared_uid: string,
) {
  try {
    const response = await stOperationService.getPositiveOrNegetiveST(
      oauthTokenHeader,
      queryId,
      keyword_shared_uid,
      website_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access Search Term");
    } else if (response.status === 200) {
      const responseData = response?.data?.data?.query_output ?? [];
      store.dispatch(setNegetiveSearchTerm(responseData)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(setNegetiveSearchTerm([]));
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function removeNegativeTermsAi(
  oauthTokenHeader: string,
  data: any,
  keyword_shared_uid: string,
) {
  try {
    const response = await stOperationService.removeNegativeTermsAi(
      oauthTokenHeader,
      data,
      keyword_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access Search Term");
    } else if (response.status === 200) {
      store.dispatch(setPositiveRecentSearchTerm(response?.data?.data ?? []));
      return response;
    }
  } catch (error: any) {
    const status = error?.status || 500;
    store.dispatch(
      setDisplayErrorSnackBar({
        message: error?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    store.dispatch(setPositiveRecentSearchTerm([]));
    return status;
  }
}
async function updateRecommendation(
  oauthTokenHeader: string,
  data: any,
  shared_uid: string,
) {
  try {
    const response = await stOperationService.updateRecommendation(
      oauthTokenHeader,
      data,
      shared_uid,
    );
    if (!response || !response.status) {
      throw new Error(
        `Failed to update recommendation for ${shared_uid}: No response`,
      );
    }

    if (response.status === 200 || response.status === 201) {
      return {
        success: true,
        status: response.status,
        data: response.data,
        shared_uid,
      };
    }

    // Handle other status codes as failures
    throw new Error(
      `Failed to update recommendation for ${shared_uid}: Status ${response.status}`,
    );
  } catch (e: any) {
    console.error(`Error in updateRecommendation for ${shared_uid}:`, e);

    // Don't dispatch error snackbar here - let the parent handler manage UI feedback
    // This allows us to show a single summary message instead of multiple error toasts

    // Re-throw with additional context
    const errorMessage =
      e?.parsedText || e?.message || "Failed to update recommendation";
    throw new Error(`${errorMessage} (ID: ${shared_uid})`);
  }
}

async function getAllExactST(
  oauthTokenHeader: string,
  queryId: string,
  ad_shared_uid: string,
  website_shared_uid: string,
) {
  try {
    const response = await stOperationService.getAllExactST(
      oauthTokenHeader,
      queryId,
      ad_shared_uid,
      website_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access Search Term");
    } else if (response.status === 200) {
      const responseData = response?.data?.data?.query_output ?? [];
      store.dispatch(setAllExactST(responseData)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(setAllExactST([]));
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function getAllSTLevel(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string,
  scope_shared_uid: string,
) {
  try {
    const response = await stOperationService.getAllSTLevel(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      account_shared_uid,
      scope_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access Search Term.");
    } else if (response.status === 200) {
      const responseData = response?.data?.data?.query_output ?? [];
      store.dispatch(setAllSTLevels(responseData));
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(setAllSTLevels([]));
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}
// get captured by multiple keywords
async function getCapturedByMultipleKeywords(
  oauthTokenHeader: string,
  queryId: string,
  website_shared_uid: string,
  account_shared_uid: string,
) {
  try {
    const response = await stOperationService.getCapturedByMultipleKeywords(
      oauthTokenHeader,
      queryId,
      website_shared_uid,
      account_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error(
        "Failed to access multiple search terms that are marked as Allowed.",
      );
    } else if (response.status === 200) {
      const capturedByMultipleKeywords =
        response?.data?.data?.query_output ?? [];
      store.dispatch(setCapturedByMultipleKeywords(capturedByMultipleKeywords)); // Set the fetched data
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(setAllSTLevels([]));
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

// active or deactive positive links (Exclusive or Allowed links active or inactive)
async function activeOrInactivePositiveLinks(
  data: any,
  oauthTokenHeader: string,
) {
  try {
    const response = await stOperationService.activeOrInactivePositiveLinks(
      data,
      oauthTokenHeader,
    );
    if (!response || !response.status) {
      throw new Error("Failed to active or inactive positive links.");
    } else if (response.status === 200) {
      return response;
    } else if (response.status === 207) {
      return response;
    }
  } catch (e: any) {
    throw e;
  }
}

async function getKeywordDetails(
  queryId: string,
  oauthTokenHeader: string,
  website_shared_uid: string,
  scope_shared_uid: string,
  search_term: string,
  search_term_match_type: string,
) {
  try {
    const response = await stOperationService.getKeywordDetails(
      queryId,
      oauthTokenHeader,
      search_term,
      search_term_match_type,
      scope_shared_uid,
      website_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access Keywords");
    } else if (response.status === 200) {
      const responseData = response?.data?.data?.query_output ?? [];
      store.dispatch(setKewordDetails(responseData));
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(setKewordDetails([]));
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

async function getKeywordDetailsNegetive(
  queryId: string,
  oauthTokenHeader: string,
  website_shared_uid: string,
  scope_shared_uid: string,
  search_term: string,
  search_term_match_type: string,
) {
  try {
    const response = await stOperationService.getKeywordDetails(
      queryId,
      oauthTokenHeader,
      search_term,
      search_term_match_type,
      scope_shared_uid,
      website_shared_uid,
    );
    if (!response || !response.status) {
      throw new Error("Failed to access Keywords");
    } else if (response.status === 200) {
      const responseData = response?.data?.data?.query_output ?? [];
      store.dispatch(setNegativeKeywordDetails(responseData));
      return response.status;
    }
  } catch (e: any) {
    const status = e?.status || 500;
    store.dispatch(setNegativeKeywordDetails([]));
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
    return status;
  }
}

export const STOperationEndPointLogic = {
  getAccounts,
  getScopes,
  getCampaignsWithoutScope,
  getNegativeDefinitionBothListAndAdgroup,
  getNegativeDefinitionInCampaign,
  createNewScope,
  linkBetweenCampaignToScope,
  removeNegativeTerms,
  updateExistingScope,
  unlinkCampaigsFromCurrentScope,
  getSTPerformanceUnclassified,
  createPositiveLinks,
  addNegativeTermsToAdgroup,
  getESTMissingCrossNegatives,
  getContradictingSTDefinitions,
  getIdenticalToExactKeyword,
  getCapturedByMultipleKeywords,
  activeOrInactivePositiveLinks,
  getSTOperationAIRecommendation,
  getPositiveST,
  getNegetiveST,
  removeNegativeTermsAi,
  updateRecommendation,
  getAllExactST,
  getAllSTLevel,
  getKeywordDetails,
  getKeywordDetailsNegetive,
  createPositiveLinksMulti,
  addNegativeTermsToAdgroupUpdate,
};
